/** Roles and permissions: the built-in roles, the permission list and a member's effective permissions. */

/** The built-in roles: seeded into every workspace; their permissions, names and descriptions are only defaults. */
export const ROLES = ['admin', 'media_manager', 'editor', 'viewer'] as const;
export type BuiltinRole = (typeof ROLES)[number];
/** A role key: a built-in or a custom role defined in the workspace (lowercase a-z, 0-9, underscore). */
export type Role = string;
export const ROLE_KEY = /^[a-z0-9_]{1,40}$/;
export const ROLE_LABELS: Record<BuiltinRole, string> = {
  admin: 'Administrator',
  media_manager: 'Media Manager',
  editor: 'Editor',
  viewer: 'Viewer',
};
export const ROLE_DESCRIPTIONS: Record<BuiltinRole, string> = {
  admin: 'Everything, including metadata models, members and roles',
  media_manager: 'Runs the library: storage, workflows, restricted access',
  editor: 'Uploads, edits metadata, cuts sequences and exports',
  viewer: 'Browses and plays',
};

/** A workspace role as the API returns it. Everyone gets key, name, builtin and position; administrators the rest. */
export interface RoleDTO {
  key: string;
  name: string;
  builtin: boolean;
  position: number;
  description?: string;
  permissions?: Permission[];
  memberCount?: number;
  apiKeyCount?: number;
  usage?: RoleUsage;
}
export interface RoleUsage {
  members: number;
  apiKeys: number;
  workflows: { id: string; name: string }[];
  pendingApprovals: number;
  fields: { id: string; label: string }[];
}

export const PERMISSIONS = [
  'asset.read',
  'asset.upload',
  'asset.edit_metadata',
  'asset.delete',
  'asset.delete_versions',
  'asset.download',
  'asset.share',
  'processing.retry',
  'storage.manage',
  'sequence.edit',
  'sequence.take_over',
  'export.create',
  'automation.manage',
  'automation.run',
  'access.manage',
  'asset.classify',
  'collection.public',
  'asset.qc',
  'qc.rerun',
  'portal.manage',
  'portal.review',
  'portal.assign',
  'group.manage',
  'rights.manage',
  'admin',
] as const;
export type Permission = (typeof PERMISSIONS)[number];

export const PERMISSION_LABELS: Record<Permission, { label: string; help: string }> = {
  'asset.read': { label: 'Browse and play', help: 'Search the library, open assets and play proxies' },
  'asset.upload': { label: 'Upload', help: 'Upload new media and new versions' },
  'asset.edit_metadata': { label: 'Edit metadata', help: 'Change metadata values the field permissions allow' },
  'asset.delete': { label: 'Delete', help: 'Remove assets' },
  'asset.delete_versions': { label: 'Delete old versions', help: 'Move earlier versions of assets to the recycle bin, one at a time or found by a scan (Administration → Storage → Old versions), and restore or purge them there' },
  'asset.download': { label: 'Download originals', help: 'Download full-quality original files' },
  'asset.share': { label: 'Share links and ask for approval', help: 'Make public links to an asset’s proxy (with an expiry and optional download), revoke them, and ask someone to approve an asset' },
  'processing.retry': { label: 'Retry processing', help: 'Retry, dismiss and restore failed processing' },
  'storage.manage': { label: 'Manage storage', help: 'Storage policy, locations, agents and transfers' },
  'sequence.edit': { label: 'Edit sequences', help: 'Create and edit sequences and subtitles' },
  'sequence.take_over': { label: 'Take over sequences', help: 'Take over a sequence someone else is editing; their edits are saved first' },
  'export.create': { label: 'Export', help: 'Render sequence exports' },
  'automation.manage': { label: 'Manage workflows', help: 'Build, publish, run and cancel workflows' },
  'automation.run': { label: 'Run workflows', help: 'Start published workflows on assets and sequences' },
  'access.manage': { label: 'Manage restricted access', help: 'Create access groups, restrict assets and see every restricted asset' },
  'asset.classify': { label: 'Set asset classes', help: 'Change which class (programme, clip, …) an asset belongs to' },
  'collection.public': { label: 'Curate public collections', help: 'Create, rename and fill the collections every member sees' },
  'asset.qc': { label: 'Run quality control', help: 'Run QC on an asset (once per version) and accept QC findings' },
  'qc.rerun': { label: 'Run QC again', help: 'Run QC again on a version that was already checked (with Run quality control; administrators have it)' },
  'portal.manage': { label: 'Manage the media portal', help: 'Administration → Media portal: supplier companies and users, delivery specs, upload rules, branding and the portal settings' },
  'portal.review': { label: 'Review portal uploads', help: 'Intake: see, accept, reject and comment on what suppliers upload through the media portal' },
  'portal.assign': { label: 'Assign deliveries', help: 'Assign placeholders to portal companies or users for delivery, and edit or cancel those assignments' },
  'group.manage': { label: 'Manage series and seasons', help: 'Create, edit, order and delete asset groups (series, seasons and the other group types), and put assets into them or take them out' },
  'rights.manage': { label: 'Manage rights', help: 'Change rights fields (status, holder, dates, restrictions, credit) and the rights defaults of watch folders and live sources, and override rights blocks with a reason (1.8.0)' },
  admin: { label: 'Administer', help: 'Metadata models, members and permissions' },
};

export const ROLE_PERMISSIONS: Record<BuiltinRole, readonly Permission[]> = {
  admin: PERMISSIONS,
  media_manager: ['asset.read', 'asset.upload', 'asset.edit_metadata', 'asset.delete', 'asset.delete_versions', 'asset.download', 'asset.share', 'processing.retry', 'storage.manage', 'sequence.edit', 'sequence.take_over', 'export.create', 'automation.manage', 'automation.run', 'access.manage', 'asset.classify', 'collection.public', 'asset.qc', 'portal.manage', 'portal.review', 'portal.assign', 'group.manage', 'rights.manage'],
  editor: ['asset.read', 'asset.upload', 'asset.edit_metadata', 'asset.download', 'asset.share', 'processing.retry', 'sequence.edit', 'export.create', 'automation.run', 'collection.public', 'group.manage'],
  viewer: ['asset.read'],
};

/**
 * A member's effective permissions: the role's set, plus per-member grants,
 * minus per-member revocations. The Administrator role always has every
 * permission and cannot lose `admin`, so a workspace cannot lock itself out;
 * no other role gets `admin` from the role (it is never granted per member
 * either, but stored grants are left alone).
 */
export function effectivePermissions(
  rolePermissions: readonly string[], grants: readonly string[] = [], revokes: readonly string[] = [], isAdminRole = false,
): Permission[] {
  const known = (p: string): p is Permission => (PERMISSIONS as readonly string[]).includes(p);
  const set = new Set<Permission>(isAdminRole ? PERMISSIONS : rolePermissions.filter(known).filter((p) => p !== 'admin'));
  for (const g of grants) if (known(g)) set.add(g);
  for (const r of revokes) if (r !== 'admin' || !isAdminRole) set.delete(r as Permission);
  return PERMISSIONS.filter((p) => set.has(p));
}
